import {
	type ProjectType,
	CORE_PROJECT,
	SERVICE_DESK_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
	SOFTWARE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types';
import type { Locale } from '@atlassian/jira-common-constants/src/supported-locales';
import { shouldApplyPermissionsGate } from '@atlassian/jira-common-selectors/src/edition';
import type { MessageDescriptorV2 } from '@atlassian/jira-intl/src/v2/types';
import { getApplicationForProject } from '@atlassian/jira-shared-types/src/application.tsx';
import { getEdition } from '@atlassian/jira-shared-types/src/edition.tsx';
import type { ProjectId } from '../../model/types';
import type { ProjectContext } from '../../services/project/types';
import type { AppState } from '../types';
import messages from './messages';
import type { ContextState } from './types';

const contextSelector = (state: AppState): ContextState => state.context;

export const projectContextSelector = (state: AppState): ProjectContext => {
	const context = contextSelector(state);
	return {
		baseUrl: '',
		projectId: context.projectId,
		projectType: context.projectType,
	};
};

/** @deprecated baseUrl in cloud is an empty string. remove code that checks or uses it. */
export const getBaseUrlSelector = (_: unknown): string => '';
export const getProjectIdSelector = (state: AppState): ProjectId =>
	contextSelector(state).projectId;
export const getLocale = (state: AppState): Locale => contextSelector(state).locale;
export const getProjectTypeSelector = (state: AppState): ProjectType =>
	contextSelector(state).projectType;

export const getSimplifiedSelector = (state: AppState): boolean =>
	contextSelector(state).simplified;

export const getProjectTypeMessageSelector = (state: AppState): MessageDescriptorV2 | undefined => {
	const { projectType } = contextSelector(state);

	switch (projectType) {
		case CORE_PROJECT:
			return messages.coreProjectTypeValueSpork;
		case SERVICE_DESK_PROJECT:
			return messages.serviceDeskProjectTypeValue;
		case SOFTWARE_PROJECT:
			return messages.softwareProjectTypeValueSpork;
		case PRODUCT_DISCOVERY_PROJECT:
			return messages.productDiscoveryTypeValue;
		default:
			return undefined;
	}
};

export const getOnDetailsUpdated = (state: AppState) => contextSelector(state).onDetailsUpdated;
export const getIsAdmin = (state: AppState) => contextSelector(state).isAdmin;
export const shouldDisableAccessLevelSelector = (state: AppState) => {
	const { appEditions } = contextSelector(state);
	const applicationForProject = getApplicationForProject(getProjectTypeSelector(state));
	const edition = getEdition(applicationForProject, appEditions);
	return shouldApplyPermissionsGate(applicationForProject, edition);
};
